
import { Movie } from "@/types/movie";
import { useUnifiedMovieState } from "./movie/useUnifiedMovieState";
import { useFavoriteToggle } from "./movie/useFavoriteToggle";
import { usePlanToWatchToggle } from "./movie/usePlanToWatchToggle";
import { useMovieDelete } from "./movie/useMovieDelete";
import { useMovieManagement } from "./movie/useMovieManagement";
import { useCommitToggle } from "./movie/useCommitToggle";

/**
 * Hook that combines all movie operations with the unified state
 * UPDATED: Now uses universal metadata architecture throughout
 */
export const useMovieOperations = () => {
  const unifiedState = useUnifiedMovieState();
  
  // Movie operations hooks using universal metadata
  const toggleFavorite = useFavoriteToggle(
    unifiedState.allMovies, 
    unifiedState.pendingActions, 
    unifiedState.addPendingAction,
    unifiedState.removePendingAction,
    unifiedState.updateMovieState
  );
  
  const togglePlanToWatch = usePlanToWatchToggle(
    unifiedState.allMovies, 
    unifiedState.pendingActions, 
    unifiedState.addPendingAction,
    unifiedState.removePendingAction,
    unifiedState.updateMovieState
  );
  
  const deleteMovie = useMovieDelete(
    unifiedState.pendingActions, 
    unifiedState.removePendingAction,
    unifiedState.removeMovieFromState
  );
  
  const { 
    addMovie: handleAddMovie, 
    updateMovie: handleUpdateMovie 
  } = useMovieManagement(unifiedState.addMovie, unifiedState.updateMovie);

  // FIXED: Use commit toggle with universal metadata architecture
  const { 
    commitToggleFavorite,
    commitTogglePlanToWatch
  } = useCommitToggle(
    unifiedState.allMovies,
    unifiedState.addMovie,
    unifiedState.updateMovieState,
    unifiedState.getVisualState
  );

  // Universal undo system
  const undoAction = (actionId: string) => {
    unifiedState.undoPendingAction(actionId);
  };

  // Return the full object structure using universal system
  const getVisualState = (tmdbId: number, actionType: 'favorite' | 'planToWatch') => {
    return unifiedState.getVisualState(tmdbId, actionType);
  };

  // Legacy compatibility functions - updated to use universal TMDB IDs
  const handleMovieRemoval = (movie: Movie, listName: string) => {
    if (!movie.tmdbId) {
      console.warn("Cannot remove movie without tmdbId:", movie);
      return;
    }
    
    // Convert to the universal system
    if (listName === 'favorites') {
      toggleFavorite(movie.tmdbId);
    } else if (listName === 'watchlist') {
      togglePlanToWatch(movie.tmdbId);
    }
  };

  const isPending = (tmdbId: number, listName: string) => {
    const actionType = listName === 'favorites' ? 'favorite' : 'planToWatch';
    return unifiedState.isPendingAction(tmdbId, actionType);
  };

  return {
    // All unified state properties with universal metadata
    allMovies: unifiedState.allMovies,
    favoriteMovies: unifiedState.favoriteMovies,
    unseenDirectorMovies: unifiedState.unseenDirectorMovies,
    planToWatchMovies: unifiedState.planToWatchMovies,
    isLoading: unifiedState.isLoading, // Includes filmography loading
    movieLookup: unifiedState.movieLookup,
    recentlyRemovedMovies: unifiedState.recentlyRemovedMovies,
    pendingActions: unifiedState.pendingActions,
    viewMode: "grid" as "grid" | "list",
    
    // State updaters and operations using universal metadata
    setAllMovies: unifiedState.setAllMovies,
    addMovie: handleAddMovie,
    updateMovie: handleUpdateMovie,
    updateMovieState: unifiedState.updateMovieState,
    removeMovieFromState: unifiedState.removeMovieFromState,
    updateViewMode: () => {}, // No-op - view mode is now managed by URL parameters
    refetchMovies: unifiedState.refetchMovies,
    addOrUpdateMovie: unifiedState.addOrUpdateMovie,
    
    // Operations using universal metadata
    deleteMovie,
    toggleFavorite,
    togglePlanToWatch,
    commitToggleFavorite,
    commitTogglePlanToWatch,
    undoAction,
    
    // Pending actions
    getPendingAction: unifiedState.getPendingAction,
    undoPendingAction: unifiedState.undoPendingAction,
    addPendingAction: unifiedState.addPendingAction,
    removePendingAction: unifiedState.removePendingAction,
    
    // Legacy compatibility
    handleMovieRemoval,
    isPending,
    
    // Interface compatibility
    isPendingAction: unifiedState.isPendingAction,
    getVisualState,
    undoRemoval: unifiedState.undoRemoval,
    
    // Utility functions
    getCardVariant: (isSearchResult: boolean = false): "grid" | "list" => {
      if (isSearchResult) return "list";
      return "grid";
    }
  };
};
